import { useMutation } from '@apollo/client';
import { IconButton, Split, SplitSection, SvgChevronDown } from '@loomhq/lens';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showErrorBar } from 'actions/errorBar';
import { ERROR } from 'constants/error-severities';
import {
  ONBOARDING_FLOW_COMPLETE,
  ONBOARDING_STEP_BACK,
} from 'constants/events';
import { SLACKID_ONBOARDING_MODAL } from 'constants/onboarding';
import * as analytics from 'utilities/analytics';
import * as localStorage from 'utilities/localStorage';
import * as logger from 'utilities/loggerx';

import { useGetUserOnboardingPersonaInfo } from 'hooks/workspaceOnboarding';

import markUserOnboardingComplete from './markUserOnboardingComplete.graphql';
import { SLACKID_ONBOARDING_PROGRESS_LOCALSTORAGE_KEY } from './constants/localStorage';
import { MAP_KEY_TO_STEP, STEPS } from './constants/steps';
import { getStepComponentFromKey } from './utilities';

import { useGetOnboardingPath } from 'components/welcome-webapp-simplified/hooks';

import styles from './OnboardingChecklist.module.less';

function ProgressBar({ currentStepIndex, steps }) {
  const progress = MAP_KEY_TO_STEP[steps[currentStepIndex]];

  return (
    <div className={styles.progressBar}>
      {steps.slice(0, 4).map((step, i) => (
        <div
          key={i}
          className={classnames(
            styles.progressItem,
            i <= progress ? styles.complete : styles.incomplete
          )}
        />
      ))}
    </div>
  );
}

function OnboardingChecklist({ closeChecklist, setShowCompletedBanner }) {
  const dispatch = useDispatch();

  const onboardingPath = Object.keys(STEPS).map(step => STEPS[step]);
  const personaObject = useGetUserOnboardingPersonaInfo();

  const userPersona = useSelector(state => state.welcome?.use_case_plan);

  const savedProgress =
    localStorage.getLocalStorageKey(
      SLACKID_ONBOARDING_PROGRESS_LOCALSTORAGE_KEY
    )?.lastCompletedStep ?? 0;

  // If the user doesn't have a persona (work, education, personal) saved, then reset their progress
  // If they do, then use localStorage to get the last step they completed and then go to the next step
  const initialStep =
    userPersona && Number.isInteger(savedProgress) ? savedProgress + 1 : 0;

  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);
  const [foundSuggestedWorkspaces, setFoundSuggestedWorkspaces] = useState(
    false
  );
  const [requestedJoin, setRequestedJoin] = useState(false);

  // Check if there is a self-serve invite that signals it's an enterprise workspace
  const { selfServeInvitePlan } = useGetOnboardingPath();

  const handleCompleteOnboardingError = error => {
    dispatch(showErrorBar(ERROR, 'An error occurred completing onboarding'));

    logger.error(error, {
      message: 'Error completing Slack account onbloarding',
    });
  };

  const [
    markOnboardingComplete,
    { loading: markOnboardingCompleteLoading },
  ] = useMutation(markUserOnboardingComplete, {
    onCompleted: data => {
      const { success, error } = data.markUserOnboardingComplete;

      if (success) {
        setShowCompletedBanner(true);
        closeChecklist();

        analytics.track(ONBOARDING_FLOW_COMPLETE, {
          step: currentStep,
          source: SLACKID_ONBOARDING_MODAL,
          ...personaObject,
        });

        return;
      }

      handleCompleteOnboardingError(new Error(error || 'unknown error'));
    },
    onError: error => {
      handleCompleteOnboardingError(error);
    },
  });

  // Safeguard for bug where the user has already completed onboarding but is still being shown the onboarding flow
  if (currentStepIndex >= onboardingPath.length) {
    logger.error(new Error('Invalid current step index'), {
      message:
        'Slack ID Onboarding Checklist step index exceeded number of steps in onboarding path',
    });

    return null;
  }

  // eslint-disable-next-line prefer-const
  let currentStep = onboardingPath[currentStepIndex];
  const CurrentStepComponent = getStepComponentFromKey(currentStep);

  function handleNextStep(incrementStepIndex = 1) {
    const nextIndex = currentStepIndex + incrementStepIndex;

    // If the step is incremented by 2, then save the current step + 1 as the last completed step
    const saveStep =
      incrementStepIndex > 1
        ? currentStepIndex + (incrementStepIndex - 1)
        : currentStepIndex;

    // Save onboarding progress in localstorage
    localStorage.setLocalStorageKey(
      SLACKID_ONBOARDING_PROGRESS_LOCALSTORAGE_KEY,
      {
        lastCompletedStep: saveStep,
      }
    );

    // Check if there is a next step or the user has completed onboarding
    const nextStepKey = onboardingPath[nextIndex];

    if (!nextStepKey) {
      // mark onboarding as complete
      markOnboardingComplete();

      return;
    }

    setCurrentStepIndex(nextIndex);
  }

  function handlePrevStep(decrementStepIndex = 1) {
    const nextStepIndex = currentStepIndex - decrementStepIndex;

    analytics.track(ONBOARDING_STEP_BACK, {
      fromStep: currentStep,
      toStep: onboardingPath[nextStepIndex],
      source: SLACKID_ONBOARDING_MODAL,
    });

    setCurrentStepIndex(nextStepIndex);
  }

  return (
    <div className={styles.checklistContainer}>
      <div className={styles.progressContainer}>
        <Split justifyContent="space-between">
          <SplitSection>
            <ProgressBar
              currentStepIndex={currentStepIndex}
              steps={onboardingPath}
            />
          </SplitSection>
          <SplitSection>
            <IconButton
              altText="minimize"
              icon={<SvgChevronDown />}
              onClick={closeChecklist}
            />
          </SplitSection>
        </Split>
      </div>

      <div className={styles.contentContainer}>
        <CurrentStepComponent
          handleNextStep={handleNextStep}
          handlePrevStep={handlePrevStep}
          foundSuggestedWorkspaces={foundSuggestedWorkspaces}
          setFoundSuggestedWorkspaces={setFoundSuggestedWorkspaces}
          requestedJoin={requestedJoin}
          setRequestedJoin={setRequestedJoin}
          selfServeInvitePlan={selfServeInvitePlan}
          markOnboardingCompleteLoading={markOnboardingCompleteLoading}
        />
      </div>
    </div>
  );
}

export default OnboardingChecklist;
